import { watch, computed } from 'vue';
import { Cookie } from '@common-vue/util/src/modules/cookie';
import getPageSpecificValues from '../utils/GoogleAdTagConstants';
import blockedList from '../utils/BlockedBrandsList';
import analyticsUtil from '../utils/analyticsUtil';
import { isSponsoredBrandExperimentEnabled } from '../utils/experimentUtil';
import L1Categories from '../utils/L1CategoryPages';

export const loadScript = (src) => new Promise((resolve, reject) => {
  const scriptEl = document.createElement('script');
  scriptEl.async = true;
  scriptEl.setAttribute('data-testid', 'securepubads');
  scriptEl.addEventListener('load', resolve, { once: true });
  scriptEl.addEventListener('error', reject, { once: true });
  document.body.appendChild(scriptEl);
  scriptEl.src = src;
});

export const isBlockedKeyword = (searchKey, siteBrand) => {
  const searchKeyword = searchKey.toLowerCase() || '';
  const keywordBlocked = siteBrand === 'mcom' ? blockedList.blockedIds.mcom.searchKeywords : blockedList.blockedIds.bcom.searchKeywords;
  const keywordException = siteBrand === 'mcom' ? blockedList.blockedIds.mcom.brandExceptions : true;
  let isKeywordBlocked = true;
  keywordBlocked.forEach((blockedKeyword) => {
    if (searchKeyword.indexOf(blockedKeyword) !== -1 && searchKeyword.indexOf(keywordException) === -1) {
      isKeywordBlocked = false;
    }
  });
  return isKeywordBlocked;
};

export const isCatBlocked = (catID, siteBrand) => {
  let isBlockedCat = true;
  const blockedCategoryIDs = blockedList.blockedIds[siteBrand].categoryIds;
  blockedCategoryIDs.forEach((blockedID) => {
    if (catID.indexOf(blockedID) !== -1) {
      isBlockedCat = false;
    }
  });
  return isBlockedCat;
};

export const isPdpBrandBlocked = (productBrand, siteBrand) => {
  const brand = productBrand ? productBrand.toLowerCase() : '';
  const { pdpBlockedBrands } = blockedList.blockedIds[siteBrand];
  let isBlockedBrand = true;
  pdpBlockedBrands.forEach((blockedBrand) => {
    if (brand.indexOf(blockedBrand) !== -1) {
      isBlockedBrand = false;
    }
  });
  return isBlockedBrand;
};

export const getTierName = (onlineUID) => {
  const tierInfo = typeof window !== 'undefined' ? localStorage.getItem(`hnfsr_${onlineUID}`) : null;
  if (tierInfo !== undefined && tierInfo !== null) {
    let tierName = null;
    try {
      tierName = JSON.parse(tierInfo).tierInfo.tierName;
    } catch (e) {
      tierName = '';
    }
    return tierName;
  }
  return null;
};

export const calculateDeviceFromWidth = (width) => {
  if (width > 1024) {
    return 'PC';
  }
  if (width <= 600) {
    return 'Phone';
  }
  return 'Tablet';
};

export const getKeyValues = (page, deviceType, brand, searchKeyword, catID, topLevelCatID, categoryIds) => {
  const tagPageType = page === 'subsplash' ? 'search' : page;
  const device = deviceType === 'mobile' ? 'mweb' : 'desktop';
  const mcomTagDevice = deviceType === 'mobile' ? 'Phone' : 'PC';
  const bcomTagDevice = typeof window !== 'undefined' ? calculateDeviceFromWidth(window.innerWidth) : '';
  const tagDevice = brand === 'bcom' ? bcomTagDevice : mcomTagDevice;
  const searchTerm = searchKeyword;
  const categoryID = catID;
  const topLevelCategoryID = topLevelCatID;
  const categoryTreeIDs = categoryIds;

  const getVisitorId = (visitorID, separator) => {
    if (visitorID.indexOf(separator) !== -1) {
      return decodeURI(visitorID).split(separator)[1].split('|')[1];
    }
    return '';
  };
  const adobeVistorId = Cookie.get('AMCV_8D0867C25245AE650A490D4C@AdobeOrg') || '';
  const visitorId = getVisitorId(adobeVistorId, 'MCMID');
  const utagMainValues = {
    utag_main_v_id: '',
    utag_main_ses_id: '',
    utag_main_sn: '',
    utag_main_ss: '',
    utag_main_st: '',
    utag_main_pn: '',
  };
  const utagMain = Cookie.get('utag_main') || '';
  const optanonConsent = Cookie.get('OptanonConsent') || 'C0005:1';
  const seedValue = Cookie.get('SEED') || '';
  const customerId = brand === 'bcom'
    ? Cookie.get('bloomingdales_online_guid') || ''
    : Cookie.get('macys_online_guid') || '';
  const onlineUID = Cookie.get('macys_online_uid') || '';

  let tierName = '';
  let userTierStatus = '';
  if (onlineUID !== undefined) {
    tierName = getTierName(onlineUID);
    userTierStatus = tierName || '';
  }
  const splitUtagMainValues = utagMain.split('$');
  splitUtagMainValues.forEach((val) => {
    const utagVal = val.split(';')[0];
    if (utagVal.indexOf('v_id') !== -1) {
      utagMainValues.utag_main_v_id = utagVal.split(':')[1];
    }
    if (utagVal.indexOf('ses_id') !== -1) {
      utagMainValues.utag_main_ses_id = utagVal.split(':')[1];
    }
    if (utagVal.indexOf('_sn') !== -1) {
      utagMainValues.utag_main_sn = utagVal.split(':')[1];
    }
    if (utagVal.indexOf('_ss') !== -1) {
      utagMainValues.utag_main_ss = utagVal.split(':')[1];
    }
    if (utagVal.indexOf('_st') !== -1) {
      utagMainValues.utag_main_st = utagVal.split(':')[1];
    }
    if (utagVal.indexOf('_pn') !== -1) {
      utagMainValues.utag_main_pn = utagVal.split(':')[1];
    }
  });
  return {
    tagPageType,
    device,
    tagDevice,
    visitorId,
    seedValue,
    customerId,
    userTierStatus,
    utagMainValues,
    searchTerm,
    categoryID,
    topLevelCategoryID,
    categoryTreeIDs,
    optanonConsent,
  };
};

export const getDefinedSlots = (id, page, keyValues, tagEnv, brand) => {
  const adData = getPageSpecificValues(page).ad_data[brand] || '';
  let adTagPage = page;
  let defineSlot = '';
  /* istanbul ignore next */
  if (page === 'subsplash') {
    adTagPage = 'search';
  } else if (page === 'browse' && id === 'GOOGLE_AD_3') {
    adTagPage = 'catsplash';
  }
  let { pos } = adData[keyValues.device][id];
  const { width, height } = adData[keyValues.device][id];
  const dynamicVars = {};
  if (id === 'googleAdOnPdpBottom' && brand === 'mcom' && keyValues.device === 'desktop') {
    adData[keyValues.device][id].forEach((adDataObj, index) => {
      pos = adDataObj.pos;
      dynamicVars[`width_${index + 1}`] = adDataObj.width;
      dynamicVars[`height_${index + 1}`] = adDataObj.height;
    });
  }
  /* istanbul ignore next */
  defineSlot = window.googletag.defineSlot(
      `/${adData.slotId}/${adData.brand}/${adTagPage}/${pos}/${keyValues.device}`,
      ['fluid', [width, height], [dynamicVars.width_1, dynamicVars.height_1], [dynamicVars.width_2, dynamicVars.height_2], [dynamicVars.width_3, dynamicVars.height_3]],
      id,
  );
  /* istanbul ignore next */
  defineSlot
    .setTargeting('ut.event', 'view')
    .setTargeting('utag_main_ss', keyValues.utagMainValues.utag_main_ss)
    .setTargeting('utag_main_sn', keyValues.utagMainValues.utag_main_sn)
    .setTargeting('utag_main_st', keyValues.utagMainValues.utag_main_st)
    .setTargeting('utag_main_v_id', keyValues.utagMainValues.utag_main_v_id)
    .setTargeting('utag_main_pn', keyValues.utagMainValues.utag_main_pn)
    .setTargeting('ut.profile', 'main')
    .setTargeting('loggedin', Cookie.get('SignedIn'))
    .setTargeting('memtype', keyValues.userTierStatus)
    .setTargeting('env', tagEnv)
    .setTargeting('utag_main_ses_id', keyValues.utagMainValues.utag_main_ses_id)
    .setTargeting('mcmid', keyValues.visitorId)
    .setTargeting('device', keyValues.tagDevice)
    .setTargeting('cid', keyValues.customerId)
    .setTargeting('site', brand)
    .setTargeting(
      'expID',
      decodeURIComponent(keyValues.seedValue).split('|').join(','),
    );
  return defineSlot;
};

export const getRefererParamOrKeyword = (ref) => {
  const referer = ref.toLowerCase();
  const searchUrl = referer.split('?')[1];
  const arr = referer.split('/');
  const isFeatured = arr.indexOf('featured');
  const isProduct = arr.indexOf('product');
  const isShopByBrand = ref.indexOf('cm_sp=shop_by_brand');
  let refCat = '';
  let refKW = '';
  if (isProduct > -1 || isShopByBrand > -1) {
    return [refCat, refKW];
  }
  if (isFeatured > -1) {
    const kw = arr[isFeatured + 1];
    if (kw.indexOf('?') > -1) {
      refKW = kw.split('?')[0];
    } else {
      refKW = kw;
    }
  } else {
    let params;
    if (searchUrl) {
      const urlSearchParams = new URLSearchParams(searchUrl);
      params = Object.fromEntries(urlSearchParams.entries());
    }
    if (params) {
      const paramId = params.id;
      const paramsKW = params.keyword;
      if (paramId) {
        refCat = paramId;
      }
      if (paramsKW) {
        refKW = paramsKW;
      }
    }
  }
  return [refCat, decodeURIComponent(refKW)];
};

export const getRefererParamOrKeywordBcom = (ref) => {
  const referer = ref.toLowerCase();
  const searchUrl = referer.indexOf('keyword') > -1 || referer.indexOf('cm_sp=navigation-_-top_nav') < 0;
  const browseUrl = (referer.search(/id=\d+/g) > -1 && referer.indexOf('shop/product') < 0) || referer.indexOf('cm_sp=navigation-_-top_nav') > -1;
  let refCat = '';
  let refKW = '';
  if (browseUrl) {
    let browseArr = referer.match(/id=\d+/g);
    if (browseArr) {
      refCat = browseArr[0].split('=')[1];
    } else {
      // eslint-disable-next-line no-useless-escape
      browseArr = referer.match(/cm\_sp\=navigation\-\_\-top\_nav\-\_\-\d+/g);
      refCat = browseArr[0].split('-_-')[2];
    }
  }
  if (searchUrl) {
    const searchArr = referer.match(/keyword=.+/g);
    if (searchArr) {
      refKW = decodeURIComponent(searchArr[0].split('=')[1]);
    }
  }
  return [refCat, refKW.replaceAll('+', ' ')];
};

export const initializeGoogleSlot = (
  pageElements,
  page,
  keyValues,
  enableGAMImpressionTags,
  enableGAMClickTags,
  refererURL,
  tagEnv,
  brand,
  productBrand,
  gamAdsLoaded,
  store,
  isFacetSelection = false,
) => {
  let elements = pageElements;
  /* istanbul ignore next */
  if (brand === 'mcom' && page === 'browse') {
    const isL1CatsplashPage = L1Categories.L1CategoryList[brand].categoryIds.indexOf(keyValues.categoryID) !== -1 || false;
    /* istanbul ignore next */
    if (isL1CatsplashPage) {
      // If it is an L1 Catsplash Page, we remove the top ad call.
      elements = pageElements.indexOf('googleAdOnSearch') !== -1 ? pageElements.splice(pageElements.indexOf('googleAdOnSearch'), 1) : pageElements;
    } else if (isSponsoredBrandExperimentEnabled()) {
      // If the Sponsored Brand Experiment is enabled, we remove both the Top as well as the L1 Catsplash ad calls.
      const topPlaceholders = keyValues.device === 'mweb' ? ['GOOGLE_AD_3'] : ['googleAdOnSearch', 'GOOGLE_AD_3'];
      topPlaceholders.forEach((element) => {
        pageElements.splice(pageElements.indexOf(element), 1);
      });
    } else {
      // If it isn't an L1 page and the Sponsored Brand Experiment is Off, we remove the L1 Ad call.
      pageElements.splice(pageElements.indexOf('GOOGLE_AD_3'), 1);
    }
    elements = pageElements;
  } else if (brand === 'mcom' && (page === 'search' || page === 'subsplash') && keyValues.device !== 'mweb') {
    /* istanbul ignore next */
    if (isSponsoredBrandExperimentEnabled()) {
      // If the Sponsored Brand Experiment is enabled, we remove the Top ad call.
      elements = pageElements.indexOf('googleAdOnSearch') !== -1 ? pageElements.splice(pageElements.indexOf('googleAdOnSearch'), 1) : pageElements;
      elements = pageElements;
    }
  }
  window.googletag = window.googletag || {
    cmd: [],
  };
  /* istanbul ignore next */
  window.googletag.cmd.push(() => {
    elements.forEach((element) => {
      const id = element;
      const defineSlot = getDefinedSlots(id, page, keyValues, tagEnv, brand);
      if (page === 'homepage') store.dispatch('googleAdStore/pushAdData', id);
      /* istanbul ignore next */
      if (page !== 'homepage') {
        /* istanbul ignore next */
        defineSlot
          .setTargeting('utag_main_sn', '')
          .setTargeting('pid', [])
          .setTargeting('oval', [])
          .setTargeting('discount', [])
          .setTargeting('oid', [])
          .setTargeting('kw', page === 'search' ? keyValues.searchTerm : [])
          .setTargeting('Brand', page === 'pdp' ? productBrand : [])
          .setTargeting('price', [])
          .setTargeting('scat', page === 'search' ? [] : keyValues.categoryID)
          .setTargeting('cat', page === 'search' ? [] : keyValues.topLevelCategoryID);
        if (keyValues.seedValue.includes('2052-21')) {
          defineSlot.setTargeting('categoryIDs', decodeURIComponent(keyValues.categoryTreeIDs).split(',').join(','));
        }
        /* istanbul ignore next */
        if (page === 'pdp') {
          let [refCat, refKW] = '';
          if (refererURL) {
            [refCat, refKW] = brand === 'mcom' ? getRefererParamOrKeyword(refererURL) : getRefererParamOrKeywordBcom(refererURL);
          }
          /* istanbul ignore next */
          defineSlot
            .setTargeting('refCat', refCat || '')
            .setTargeting('refKW', refKW || '');
        }
      }
      defineSlot.addService(window.googletag.pubads());
      gamAdsLoaded.push(id);
    });

    window.googletag.pubads().addEventListener('slotOnload', (event) => {
      const { slot } = event;
      const slotElementId = slot.getSlotElementId();
      const isIframe = document.querySelectorAll(`#${slotElementId}`)[0]?.querySelectorAll('iframe')
        ?.length > 0;
      const isSponsoredLabelMissing = document.querySelectorAll(`#${slotElementId} .google-ad-sponsored-label`)
        ?.length === 0;

      if (isIframe && isSponsoredLabelMissing && slot.getResponseInformation) {
        store.dispatch('googleAdStore/setAdLoaded', slotElementId);
        if (enableGAMImpressionTags) {
          analyticsUtil.initGAMImpressionTag(
            slotElementId,
            keyValues.device,
            page,
            brand,
          );
        }
        if (enableGAMClickTags) analyticsUtil.initGAMClickTag(slotElementId, keyValues.device, page, brand);
      }
    });

    const fetchMargin = keyValues.device === 'mweb' ? 150 : 100;
    const renderMargin = keyValues.device === 'mweb' ? 37 : 25;
    window.googletag.pubads().enableLazyLoad({
      fetchMarginPercent: fetchMargin,
      // Render slots within 2 viewports.
      renderMarginPercent: renderMargin,
    });

    window.googletag.pubads().collapseEmptyDivs();
    window.googletag.pubads().setPublisherProvidedId(keyValues.visitorId);
    window.googletag.enableServices();
  });
  window.googletag.cmd.push(() => {
    elements.forEach((element) => {
      const isInline = element.indexOf('Inline') !== -1;
      const inlineFacets = isInline && isFacetSelection;
      if (page !== 'homepage' && !inlineFacets) {
        const isElementInDOM = !!document.getElementById(element);
        if (isElementInDOM) {
          window.googletag.display(element);
        }
      } else if (inlineFacets) {
        const isAdMounted = computed(() => store.getters['googleAdStore/isAdMounted'](element));
        if (isAdMounted.value) {
          window.googletag.display(element);
        } else {
          watch(isAdMounted, () => {
            if (isAdMounted.value) {
              window.googletag.display(element);
            }
          });
        }
      } else {
        const parentNode = document.getElementById(element);
        if (parentNode) {
          window.googletag.display(element);
        }
      }
    });
  });
};

export const isGAMBlocked = (page, brand, productBrand, keyValues) => {
  let isGamBlocked = false;
  if (page === 'homepage'
    || ((page === 'search')
      && isBlockedKeyword(keyValues.searchTerm, brand))
    || ((page === 'browse' || page === 'subsplash' || page === 'catsplash')
      && isCatBlocked(keyValues.categoryID, brand))
    || (page === 'pdp' && isPdpBrandBlocked(productBrand, brand))) {
    isGamBlocked = true;
  }
  return isGamBlocked;
};

const initGAMScript = (
  pageElements,
  page,
  deviceType,
  isFacetSelection,
  enableGAMImpressionTags,
  enableGAMClickTags,
  refererURL,
  tagEnv,
  brand,
  productBrand,
  searchKeyword,
  catID,
  topLevelCatID,
  categoryTreeIDs,
  gamAdsLoaded,
  store,
) => {
  const keyValues = getKeyValues(page, deviceType, brand, searchKeyword, catID, topLevelCatID, categoryTreeIDs);

  if (page === 'homepage' && keyValues.optanonConsent.includes('C0005:1')) {
    loadScript('https://securepubads.g.doubleclick.net/tag/js/gpt.js');
  }
  if (isGAMBlocked(page, brand, productBrand, keyValues) && typeof window !== 'undefined') {
    if (window.googletag && window.googletag.apiReady && isFacetSelection) {
      window.googletag.destroySlots();
      store.dispatch('googleAdStore/removeAdLoadedOnFacetSelection');
      pageElements.forEach((id) => {
        if (id.indexOf('Inline') >= 0) {
          store.dispatch('googleAdStore/removeAdMountedOnFacetSelection', id);
        }
      });
      initializeGoogleSlot(
        pageElements,
        page,
        keyValues,
        enableGAMImpressionTags,
        enableGAMClickTags,
        refererURL,
        tagEnv,
        brand,
        productBrand,
        gamAdsLoaded,
        store,
        isFacetSelection,
      );
    } else {
      if (page !== 'homepage') {
        pageElements.forEach((id) => {
          store.dispatch('googleAdStore/pushAdData', id);
        });
      }
      initializeGoogleSlot(
        pageElements,
        page,
        keyValues,
        enableGAMImpressionTags,
        enableGAMClickTags,
        refererURL,
        tagEnv,
        brand,
        productBrand,
        gamAdsLoaded,
        store,
      );
    }
  }
};

export const useLoadGAMAd = {
  initGAMScript,
  initializeGoogleSlot,
};
